import React from "react";
import { useLocation } from "react-router-dom";
import { useBackNavigation } from "@/hooks/useBackNavigation";
import { MButton } from "./MButtons";
import MProfilePicker from "./MProfilePicker";
import { MExit } from "./MExit";
import { IconEdit } from "@tabler/icons-react";

/**
 * MTopBar: Renders a back button (using MButton with back variant) and the profile picker.
 * Place this at the top of any page to provide consistent navigation UX.
 */
interface MTopBarProps {
  hideProfilePicker?: boolean;
  onBack?: () => void;
  exitPath?: string;
  exitKey?: string;
  hideBack?: boolean;
  hideExit?: boolean; // Hide the exit button
  backUrl?: string; // Optional back path
  withBackground?: boolean; // Add white background for sticky behavior
  disableSticky?: boolean; // Disable sticky positioning
  style?: React.CSSProperties; // Custom inline styles
  onEdit?: () => void; // Edit button handler
  showEdit?: boolean; // Show edit button
  showTopBarGradient?: boolean; // Show gradient background on top bar
  showPurpleGradient?: boolean; // Opaque purple gradient (same as reminder header)
  skipExitConfirmation?: boolean; // Skip exit confirmation dialog
  // Collapse the bar's own vertical padding — for pages with their own full
  // header, where this component renders empty anyway (hideBack +
  // hideProfilePicker). Still reserves safe-area-inset-top.
  minimal?: boolean;
}

interface LocationState {
  backUrl?: string;
}

const MTopBar: React.FC<MTopBarProps> = ({
  hideProfilePicker,
  onBack,
  exitPath,
  exitKey,
  hideBack,
  hideExit,
  backUrl,
  withBackground = false,
  disableSticky = false,
  style,
  onEdit,
  showEdit,
  showTopBarGradient = false,
  showPurpleGradient = false,
  skipExitConfirmation = false,
  minimal = false,
}) => {
  const location = useLocation();
  const state = location.state as LocationState;
  const stateBackUrl = state?.backUrl;
  const resolvedBackUrl = backUrl || stateBackUrl;
  const { handleBack } = useBackNavigation({
    onBack,
    backUrl: resolvedBackUrl,
  });

  React.useEffect(() => {
    // Make back navigation callable from React Native
    (window as any).nativeSwipeBack = () => {
      handleBack();
    };
    return () => {
      (window as any).nativeSwipeBack = undefined;
    };
  }, [handleBack]);

  // Edge case padding for Family Index page
  const isFamilyListing = location.pathname === "/profile/family";
  const classNames = [
    "mtopbarContainer",
    isFamilyListing ? "mtopbar-family-extra-padding" : "",
    showPurpleGradient
      ? "with-purple-gradient"
      : withBackground
      ? "with-background"
      : "",
    disableSticky ? "non-sticky" : "",
    showTopBarGradient ? "with-gradient" : "",
    minimal ? "minimal" : "",
  ]
    .filter(Boolean)
    .join(" ");

  return (
    <div className={classNames} style={style}>
      <div className="flex items-center">
        {!hideBack && (
          <MButton variant="back" onClick={handleBack} className="mr-2">
            Back
          </MButton>
        )}
      </div>
      <div className="flex items-center gap-3">
        {showEdit && onEdit && (
          <button
            onClick={onEdit}
            className="mtopbarEditButton"
            aria-label="Edit"
          >
            <IconEdit size={20} />
          </button>
        )}
        {!hideProfilePicker && <MProfilePicker disabled={isFamilyListing} />}
        {exitPath && !hideExit && (
          <MExit
            path={exitPath}
            exitKey={exitKey}
            skipConfirmation={skipExitConfirmation}
          />
        )}
      </div>
    </div>
  );
};

export default MTopBar;
